/**
 * Files API Service
 *
 * API client for the Files module (mini-dropbox).
 * Handles all file and folder operations with the NGUI backend.
 *
 * Uses presigned URLs for large file uploads with progress tracking.
 */

import axios, { AxiosProgressEvent } from 'axios';
import apiClient from './api';
import type {
  FileNode,
  TreeNode,
  CreateFolderRequest,
  UpdateFileRequest,
  MoveFileRequest,
  CopyFileRequest,
  BulkMoveRequest,
  BulkCopyRequest,
  BulkDeleteRequest,
  FilesFilter,
  DownloadUrlResponse,
  SearchResponse,
  FileStatsResponse,
  UploadResponse,
} from '@/types/files';

// ============================================================
// Search & Navigation
// ============================================================

/**
 * Search files by query, type, or tags
 */
export const searchFiles = async (filters?: FilesFilter): Promise<SearchResponse> => {
  const params = new URLSearchParams();

  if (filters?.query) params.append('query', filters.query);
  if (filters?.file_types?.length) params.append('file_types', filters.file_types.join(','));
  if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
  if (filters?.folders_only) params.append('folders_only', 'true');
  if (filters?.files_only) params.append('files_only', 'true');
  if (filters?.limit) params.append('limit', String(filters.limit));

  const queryString = params.toString();
  const response = await apiClient.get(`/files/search${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

/**
 * Get hierarchical tree view of user's files
 */
export const getFileTree = async (
  rootId?: string | null,
  maxDepth: number = 3,
  includeFiles: boolean = true
): Promise<TreeNode[]> => {
  const params = new URLSearchParams();
  if (rootId) params.append('root_id', rootId);
  params.append('max_depth', String(maxDepth));
  params.append('include_files', String(includeFiles));

  const response = await apiClient.get(`/files/tree?${params}`);
  return response.data;
};

/**
 * List contents of a folder
 */
export const listFolder = async (
  folderId?: string | null,
  includeFiles: boolean = true
): Promise<FileNode[]> => {
  const params = new URLSearchParams();
  if (folderId) params.append('folder_id', folderId);
  params.append('include_files', String(includeFiles));

  const response = await apiClient.get(`/files/list?${params}`);
  return response.data;
};

/**
 * Get recently modified files
 */
export const getRecentFiles = async (limit: number = 10): Promise<FileNode[]> => {
  const response = await apiClient.get(`/files/recent?limit=${limit}`);
  return response.data;
};

/**
 * Get file/folder by path
 */
export const getFileByPath = async (path: string): Promise<FileNode> => {
  const response = await apiClient.get(`/files/by-path/${encodeURIComponent(path)}`);
  return response.data;
};

/**
 * Get breadcrumb path for a file/folder
 */
export const getBreadcrumb = async (fileId: string): Promise<FileNode[]> => {
  const response = await apiClient.get(`/files/${fileId.toUpperCase()}/breadcrumb`);
  return response.data;
};

// ============================================================
// File & Folder CRUD
// ============================================================

/**
 * Get file/folder info by ID
 */
export const getFile = async (fileId: string): Promise<FileNode> => {
  const response = await apiClient.get(`/files/${fileId.toUpperCase()}`);
  return response.data;
};

/**
 * Create a new folder
 */
export const createFolder = async (data: CreateFolderRequest): Promise<FileNode> => {
  const response = await apiClient.post('/files/folders', data);
  return response.data;
};

/**
 * Upload a file
 */
export const uploadFile = async (
  file: File,
  parentId?: string | null,
  description?: string,
  tags?: string[]
): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (parentId) formData.append('parent_id', parentId);
  if (description) formData.append('description', description);
  if (tags?.length) formData.append('tags', JSON.stringify(tags));

  const response = await apiClient.post('/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Upload multiple files (legacy - no byte progress)
 */
export const uploadFiles = async (
  files: File[],
  parentId?: string | null,
  onProgress?: (completed: number, total: number) => void
): Promise<UploadResponse[]> => {
  const results: UploadResponse[] = [];

  for (let i = 0; i < files.length; i++) {
    const result = await uploadFile(files[i], parentId);
    results.push(result);
    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }

  return results;
};

// ============================================================
// Presigned Upload (with real-time progress tracking)
// ============================================================

/**
 * Progress info for a single file upload
 */
export interface FileUploadProgress {
  fileId: string;
  fileName: string;
  fileSize: number;
  bytesUploaded: number;
  percentage: number;
  uploadSpeed: number;  // bytes per second
  estimatedTimeRemaining: number;  // seconds
  status: 'pending' | 'uploading' | 'completing' | 'completed' | 'failed' | 'cancelled';
  error?: string;
}

/**
 * Aggregated progress for multiple file uploads
 */
export interface UploadProgressState {
  files: FileUploadProgress[];
  totalBytes: number;
  uploadedBytes: number;
  overallPercentage: number;
  completedCount: number;
  failedCount: number;
  totalCount: number;
}

/**
 * Presigned upload URL response
 */
interface PresignedUploadResponse {
  upload_url: string;
  file_id: string;
  s3_key: string;
  expires_in: number;
}

/**
 * Upload completion response
 */
interface UploadCompleteResponse {
  success: boolean;
  file?: UploadResponse;
  message: string;
}

/**
 * Get a presigned URL for direct S3 upload
 */
const getPresignedUploadUrl = async (
  filename: string,
  contentType: string,
  fileSize: number,
  parentId?: string | null
): Promise<PresignedUploadResponse> => {
  const response = await apiClient.post('/files/upload-url', {
    filename,
    content_type: contentType,
    file_size: fileSize,
    parent_id: parentId
  });
  return response.data;
};

/**
 * Confirm upload completion
 */
const completeUpload = async (
  fileId: string,
  autoProcess: boolean = true
): Promise<UploadCompleteResponse> => {
  const response = await apiClient.post(`/files/upload-complete/${fileId}`, {
    auto_process: autoProcess
  });
  return response.data;
};

/**
 * Cancel a pending upload
 */
export const cancelUpload = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/files/upload-cancel/${fileId}`);
};

/**
 * Upload a single file with real-time progress tracking via presigned URL.
 *
 * This bypasses Lambda/API Gateway limits by uploading directly to S3.
 */
export const uploadFileWithProgress = async (
  file: File,
  parentId: string | null | undefined,
  onProgress: (progress: FileUploadProgress) => void,
  abortSignal?: AbortSignal
): Promise<UploadResponse> => {
  const startTime = Date.now();
  let lastLoaded = 0;
  let lastTime = startTime;

  const progress: FileUploadProgress = {
    fileId: '',
    fileName: file.name,
    fileSize: file.size,
    bytesUploaded: 0,
    percentage: 0,
    uploadSpeed: 0,
    estimatedTimeRemaining: 0,
    status: 'pending'
  };

  try {
    // Step 1: Get presigned URL
    const presigned = await getPresignedUploadUrl(
      file.name,
      file.type || 'application/octet-stream',
      file.size,
      parentId
    );
    progress.fileId = presigned.file_id;

    // Step 2: Upload to S3 with progress tracking
    progress.status = 'uploading';
    onProgress({ ...progress });

    await axios.put(presigned.upload_url, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      },
      signal: abortSignal,
      onUploadProgress: (event: AxiosProgressEvent) => {
        if (event.total) {
          const now = Date.now();
          const loaded = event.loaded;

          // Calculate upload speed (bytes/sec) using rolling window
          const timeDelta = (now - lastTime) / 1000; // seconds
          if (timeDelta > 0.1) { // Update every 100ms
            const bytesDelta = loaded - lastLoaded;
            progress.uploadSpeed = bytesDelta / timeDelta;
            lastLoaded = loaded;
            lastTime = now;
          }

          progress.bytesUploaded = loaded;
          progress.percentage = Math.round((loaded / event.total) * 100);

          // Calculate ETA
          const remainingBytes = event.total - loaded;
          if (progress.uploadSpeed > 0) {
            progress.estimatedTimeRemaining = Math.round(remainingBytes / progress.uploadSpeed);
          }

          onProgress({ ...progress });
        }
      }
    });

    // Step 3: Confirm upload completion
    progress.status = 'completing';
    progress.percentage = 100;
    onProgress({ ...progress });

    const result = await completeUpload(presigned.file_id);

    if (result.success && result.file) {
      progress.status = 'completed';
      onProgress({ ...progress });
      return result.file;
    } else {
      throw new Error(result.message || 'Upload confirmation failed');
    }

  } catch (error: unknown) {
    const axiosError = error as { message?: string; code?: string };
    progress.status = axiosError.code === 'ERR_CANCELED' ? 'cancelled' : 'failed';
    progress.error = axiosError.message || 'Upload failed';
    onProgress({ ...progress });

    // Try to clean up the placeholder if we have a file ID
    if (progress.fileId) {
      try {
        await cancelUpload(progress.fileId);
      } catch {
        // Ignore cleanup errors
      }
    }

    throw error;
  }
};

/**
 * Upload multiple files with real-time progress tracking.
 *
 * Files are uploaded concurrently (up to maxConcurrent) with per-file and
 * overall progress tracking.
 */
export const uploadFilesWithProgress = async (
  files: File[],
  parentId: string | null | undefined,
  onProgress: (state: UploadProgressState) => void,
  options?: {
    maxConcurrent?: number;
    autoProcess?: boolean;
    abortController?: AbortController;
  }
): Promise<UploadResponse[]> => {
  const maxConcurrent = options?.maxConcurrent || 3;
  const abortController = options?.abortController || new AbortController();

  const fileProgresses: Map<string, FileUploadProgress> = new Map();
  const results: UploadResponse[] = [];
  const errors: Error[] = [];

  // Initialize progress state
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

  const updateOverallProgress = () => {
    const progressArray = Array.from(fileProgresses.values());
    const uploadedBytes = progressArray.reduce((sum, p) => sum + p.bytesUploaded, 0);
    const completedCount = progressArray.filter(p => p.status === 'completed').length;
    const failedCount = progressArray.filter(p => p.status === 'failed').length;

    onProgress({
      files: progressArray,
      totalBytes,
      uploadedBytes,
      overallPercentage: totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0,
      completedCount,
      failedCount,
      totalCount: files.length
    });
  };

  // Create upload tasks
  const uploadTasks = files.map((file, index) => async () => {
    const tempId = `temp-${index}`;
    fileProgresses.set(tempId, {
      fileId: tempId,
      fileName: file.name,
      fileSize: file.size,
      bytesUploaded: 0,
      percentage: 0,
      uploadSpeed: 0,
      estimatedTimeRemaining: 0,
      status: 'pending'
    });
    updateOverallProgress();

    try {
      const result = await uploadFileWithProgress(
        file,
        parentId,
        (progress) => {
          // Update with real file ID once we have it
          if (progress.fileId && progress.fileId !== tempId) {
            fileProgresses.delete(tempId);
          }
          fileProgresses.set(progress.fileId || tempId, progress);
          updateOverallProgress();
        },
        abortController.signal
      );
      results.push(result);
      return result;
    } catch (error) {
      errors.push(error as Error);
      throw error;
    }
  });

  // Execute with concurrency limit
  const executing: Promise<unknown>[] = [];

  for (const task of uploadTasks) {
    const promise = task().catch(() => {}); // Catch to prevent unhandled rejection
    executing.push(promise);

    if (executing.length >= maxConcurrent) {
      await Promise.race(executing);
      // Remove completed promises
      const completed = executing.filter(p =>
        Promise.race([p, Promise.resolve('pending')]).then(
          v => v !== 'pending',
          () => true
        )
      );
      for (const done of completed) {
        const index = executing.indexOf(done);
        if (index > -1) executing.splice(index, 1);
      }
    }
  }

  // Wait for all remaining uploads
  await Promise.allSettled(executing);

  return results;
};

// ============================================================
// Batch Upload
// ============================================================

export interface BatchUploadResponse {
  uploaded: FileNode[];
  errors: Array<{ filename: string; error: string }>;
  total_uploaded: number;
  total_failed: number;
}

/**
 * Upload multiple files in a single request (batch upload)
 */
export const uploadFilesBatch = async (
  files: File[],
  parentId?: string | null,
  autoProcess: boolean = true
): Promise<BatchUploadResponse> => {
  const formData = new FormData();

  for (const file of files) {
    formData.append('files', file);
  }

  if (parentId) {
    formData.append('parent_id', parentId);
  }
  formData.append('auto_process', String(autoProcess));

  const response = await apiClient.post('/files/upload-batch', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

/**
 * Upload a directory preserving folder structure
 *
 * @param files - File list from input with webkitdirectory
 * @param parentId - Target parent folder ID
 * @param autoProcess - Auto-process supported file types
 */
export const uploadDirectory = async (
  files: FileList | File[],
  parentId?: string | null,
  autoProcess: boolean = true
): Promise<BatchUploadResponse> => {
  const formData = new FormData();
  const relativePaths: string[] = [];

  for (const file of files) {
    formData.append('files', file);
    // Use webkitRelativePath if available, otherwise just filename
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    relativePaths.push(relativePath);
  }

  formData.append('paths', JSON.stringify(relativePaths));

  if (parentId) {
    formData.append('parent_id', parentId);
  }
  formData.append('auto_process', String(autoProcess));

  const response = await apiClient.post('/files/upload-directory', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

/**
 * Update file/folder metadata
 */
export const updateFile = async (fileId: string, data: UpdateFileRequest): Promise<FileNode> => {
  const response = await apiClient.patch(`/files/${fileId.toUpperCase()}`, data);
  return response.data;
};

/**
 * Delete a file or folder
 */
export const deleteFile = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/files/${fileId.toUpperCase()}`);
};

// ============================================================
// Download
// ============================================================

/**
 * Get presigned download URL for a file
 */
export const getDownloadUrl = async (fileId: string): Promise<DownloadUrlResponse> => {
  const response = await apiClient.get(`/files/${fileId.toUpperCase()}/download`);
  return response.data;
};

/**
 * Download a file (opens in new tab or triggers download)
 */
export const downloadFile = async (fileId: string, fileName?: string): Promise<void> => {
  const { url } = await getDownloadUrl(fileId);

  // Open the presigned URL in a new tab (or trigger download via Content-Disposition)
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get file content as blob (for preview)
 */
export const getFileBlob = async (fileId: string): Promise<Blob> => {
  const { url } = await getDownloadUrl(fileId);

  // Fetch the content directly from S3
  const response = await axios.get(url, {
    responseType: 'blob',
  });

  return response.data;
};

/**
 * Get text file content (for text preview)
 */
export const getTextContent = async (fileId: string): Promise<string> => {
  const response = await apiClient.get(`/files/${fileId.toUpperCase()}/content`);
  return response.data.content;
};

// ============================================================
// Move & Copy
// ============================================================

/**
 * Move a file or folder to a new parent
 */
export const moveFile = async (fileId: string, data: MoveFileRequest): Promise<FileNode> => {
  const response = await apiClient.post(`/files/${fileId.toUpperCase()}/move`, data);
  return response.data;
};

/**
 * Copy a file or folder
 */
export const copyFile = async (fileId: string, data: CopyFileRequest): Promise<FileNode> => {
  const response = await apiClient.post(`/files/${fileId.toUpperCase()}/copy`, data);
  return response.data;
};

// ============================================================
// Bulk Operations
// ============================================================

/**
 * Move multiple files/folders
 */
export const bulkMoveFiles = async (data: BulkMoveRequest): Promise<{
  moved: string[];
  failed: Array<{ id: string; reason: string }>;
}> => {
  const response = await apiClient.post('/files/bulk/move', data);
  return response.data;
};

/**
 * Copy multiple files/folders
 */
export const bulkCopyFiles = async (data: BulkCopyRequest): Promise<{
  copied: string[];
  failed: Array<{ id: string; reason: string }>;
}> => {
  const response = await apiClient.post('/files/bulk/copy', data);
  return response.data;
};

/**
 * Delete multiple files/folders
 */
export const bulkDeleteFiles = async (data: BulkDeleteRequest): Promise<{
  deleted: string[];
  failed: Array<{ id: string; reason: string }>;
}> => {
  const response = await apiClient.post('/files/bulk/delete', data);
  return response.data;
};

/**
 * Download multiple files/folders as ZIP
 * Opens the download URL in a new tab
 */
export const downloadZip = async (fileIds: string[]): Promise<void> => {
  const response = await apiClient.post('/files/download-zip', { file_ids: fileIds });
  const { url } = response.data;
  window.open(url, '_blank');
};

// ============================================================
// Statistics
// ============================================================

/**
 * Get file statistics for the user
 */
export const getFileStats = async (): Promise<FileStatsResponse> => {
  const response = await apiClient.get('/files/stats');
  return response.data;
};

// ============================================================
// Tags
// ============================================================

/**
 * Get all unique tags used by the user
 */
export const getAllTags = async (): Promise<string[]> => {
  const response = await apiClient.get('/files/tags');
  return response.data.tags;
};

/**
 * Get files by tag
 */
export const getFilesByTag = async (tag: string, limit: number = 50): Promise<FileNode[]> => {
  const response = await apiClient.get(`/files/by-tag/${encodeURIComponent(tag)}?limit=${limit}`);
  return response.data;
};

// ============================================================
// Comments
// ============================================================

export interface FileComment {
  id: string;
  file_id: string;
  user_id: number;
  content: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
}

/**
 * Get all comments for a file
 */
export const getFileComments = async (fileId: string): Promise<FileComment[]> => {
  const response = await apiClient.get(`/files/${fileId}/comments`);
  return response.data;
};

/**
 * Add a comment to a file
 */
export const addFileComment = async (fileId: string, content: string): Promise<FileComment> => {
  const response = await apiClient.post(`/files/${fileId}/comments`, { content });
  return response.data;
};

/**
 * Update a comment
 */
export const updateFileComment = async (
  fileId: string,
  commentId: string,
  content: string
): Promise<FileComment> => {
  const response = await apiClient.patch(`/files/${fileId}/comments/${commentId}`, { content });
  return response.data;
};

/**
 * Delete a comment
 */
export const deleteFileComment = async (fileId: string, commentId: string): Promise<void> => {
  await apiClient.delete(`/files/${fileId}/comments/${commentId}`);
};

/**
 * Get comment count for a file
 */
export const getFileCommentCount = async (fileId: string): Promise<number> => {
  const response = await apiClient.get(`/files/${fileId}/comments/count`);
  return response.data.count;
};

// ============================================================
// Re-export types
// ============================================================

export type {
  FileNode,
  TreeNode,
  BreadcrumbItem,
  FilesViewMode,
  FilesSortBy,
  FilesSortOrder,
  FilesViewState,
  FileTypeConfig,
  CreateFolderRequest,
  UploadFileRequest,
  UpdateFileRequest,
  MoveFileRequest,
  CopyFileRequest,
  BulkMoveRequest,
  BulkCopyRequest,
  BulkDeleteRequest,
  FilesFilter,
  DownloadUrlResponse,
  SearchResponse,
  FileStatsResponse,
  UploadResponse,
  DragItem,
  DropResult,
  FilesSelection,
} from '@/types/files';

// Re-export helper functions
export {
  getFileTypeConfig,
  formatFileSize,
  formatFileDate,
  getFileExtension,
  isImageFile,
  isTextFile,
  isPdfFile,
  isAudioFile,
  isVideoFile,
  sortFiles,
  buildBreadcrumbs,
  getMimeTypeFromExtension,
  FILE_TYPE_CONFIG,
} from '@/types/files';

// ============================================================
// Advanced Search Types
// ============================================================

export interface SearchResult {
  file_id: string;
  name: string;
  path: string | null;
  content_type: string | null;
  file_size: number | null;
  rank: number | null;
  similarity: number | null;
  hybrid_score: number | null;
  headline: string | null;
  chunk_text: string | null;
}

export interface AdvancedSearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  mode: 'fulltext' | 'semantic' | 'hybrid' | 'by-type' | 'by-tags' | 'by-name';
}

// ============================================================
// Processing Types
// ============================================================

export type ProcessingLevel = 'basic' | 'scout' | 'full' | 'ai';

export interface ProcessingStatus {
  file_id: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  processing_level: ProcessingLevel | null;
  has_preview: boolean;
  has_markdown: boolean;
  has_ocr: boolean;
  has_embeddings: boolean;
  has_ascii: boolean;
  has_sense: boolean;
  has_scout: boolean;
  has_page_previews: boolean;
  ascii_page_count: number | null;
  error_message: string | null;
}

export interface PreviewResponse {
  file_id: string;
  preview_url: string;
  width: number | null;
  height: number | null;
}

export interface MarkdownResponse {
  file_id: string;
  markdown: string;
  title: string | null;
}

export interface TextResponse {
  file_id: string;
  text: string;
  source: 'markdown' | 'ocr' | 'content_text';
}

// ============================================================
// Mining Types
// ============================================================

export interface MiningRequest {
  request: string;
  schema: Record<string, unknown>;
}

export interface MiningResult {
  file_id: string;
  items: Record<string, unknown>[];
  page_count: number;
  cached: boolean;
  schema_hash: string;
}

export interface MiningCorrection {
  field_name: string;
  wrong_value: string;
  correct_value: string;
  reason?: string;
}

export interface CorrectionItem {
  id: string;
  field_name: string;
  wrong_value: string;
  correct_value: string;
  reason: string | null;
  created_at: string;
}

export interface CorrectionsResponse {
  file_id: string;
  corrections: CorrectionItem[];
  total: number;
}

// ============================================================
// ASCII Rendering Types
// ============================================================

export interface AsciiPage {
  file_id: string;
  page_number: number;
  ascii_content: string;
  visual_markers: Array<{
    type: string;
    content: string;
    location?: string;
  }>;
}

export interface AsciiPagesResponse {
  file_id: string;
  pages: AsciiPage[];
  total_pages: number;
}

export interface AsciiJobStatus {
  job_id: string;
  file_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  pages_total: number | null;
  pages_processed: number | null;
  error_message: string | null;
}

// ============================================================
// Sense Summary Types
// ============================================================

export interface SenseSummary {
  file_id: string;
  summary_text: string;
  summary_length: number;
  key_topics: string[];
  entity_mentions: string[];
  pages_summarized: number | null;
  model: string;
  generated_at: string | null;
  generation_cost_usd: number | null;
}

export interface ScoutResult {
  file_id: string;
  scouted_text: string | null;
  summary: string | null;
  doc_type: string | null;
  key_topics: string[];
  entities: { name: string; type: string }[];
  model: string | null;
  cost_usd: number | null;
  pages_scouted: number | null;
  created_at: string | null;
}

export interface SenseJobStatus {
  job_id: string;
  file_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  pages_total: number | null;
  pages_processed: number | null;
  current_summary_length: number | null;
  error_message: string | null;
  cost_usd: number | null;
}

// ============================================================
// Advanced Search API
// ============================================================

/**
 * Full-text search using PostgreSQL tsvector
 */
export const searchFulltext = async (
  query: string,
  limit: number = 20,
  offset: number = 0
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
    offset: String(offset),
  });
  const response = await apiClient.get(`/search/fulltext?${params}`);
  return response.data;
};

/**
 * Semantic search using pgvector embeddings
 */
export const searchSemantic = async (
  query: string,
  limit: number = 20
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });
  const response = await apiClient.get(`/search/semantic?${params}`);
  return response.data;
};

/**
 * Hybrid search combining full-text and semantic search (RRF)
 */
export const searchHybrid = async (
  query: string,
  limit: number = 20,
  fulltextWeight: number = 0.5
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
    fulltext_weight: String(fulltextWeight),
  });
  const response = await apiClient.get(`/search/hybrid?${params}`);
  return response.data;
};

/**
 * Search files by content type
 */
export const searchByType = async (
  types: string[],
  limit: number = 50
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    types: types.join(','),
    limit: String(limit),
  });
  const response = await apiClient.get(`/search/by-type?${params}`);
  return response.data;
};

/**
 * Search files by tags
 */
export const searchByTags = async (
  tags: string[],
  matchAll: boolean = true,
  limit: number = 50
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    tags: tags.join(','),
    match_all: String(matchAll),
    limit: String(limit),
  });
  const response = await apiClient.get(`/search/by-tags?${params}`);
  return response.data;
};

/**
 * Search files by name using fuzzy matching
 */
export const searchByName = async (
  query: string,
  limit: number = 50
): Promise<AdvancedSearchResponse> => {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });
  const response = await apiClient.get(`/search/by-name?${params}`);
  return response.data;
};

// ============================================================
// Document Processing API
// ============================================================

/**
 * Trigger document processing for a file
 */
export const triggerProcessing = async (
  fileId: string,
  level: ProcessingLevel = 'full'
): Promise<ProcessingStatus> => {
  const response = await apiClient.post(`/processing/${fileId.toUpperCase()}/process`, {
    level,
  });
  return response.data;
};

/**
 * Get processing status for a file
 */
export const getProcessingStatus = async (fileId: string): Promise<ProcessingStatus> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/processing-status`);
  return response.data;
};

/**
 * Get preview thumbnail URL for a file
 */
export const getPreviewUrl = async (
  fileId: string,
  size: 'sm' | 'md' | 'lg' = 'md'
): Promise<PreviewResponse> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/preview?size=${size}`);
  return response.data;
};

/**
 * Get preview image as blob
 */
export const getPreviewBlob = async (
  fileId: string,
  size: 'sm' | 'md' | 'lg' = 'md'
): Promise<Blob> => {
  const { preview_url } = await getPreviewUrl(fileId, size);
  const response = await axios.get(preview_url, { responseType: 'blob' });
  return response.data;
};

/**
 * Get markdown content for a file
 */
export const getMarkdown = async (fileId: string): Promise<MarkdownResponse> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/markdown`);
  return response.data;
};

/**
 * Get extracted text content from a file
 */
export const getExtractedText = async (fileId: string): Promise<TextResponse> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/text`);
  return response.data;
};

// ============================================================
// AI Mining API
// ============================================================

/**
 * Extract structured data from a document using GPT-5
 */
export const mineDocument = async (
  fileId: string,
  schema: Record<string, unknown>,
  request: string = 'Extract all items matching the schema'
): Promise<MiningResult> => {
  const response = await apiClient.post(`/mining/${fileId.toUpperCase()}/mine`, {
    schema,
    request,
  });
  return response.data;
};

/**
 * Get cached mining results for a file
 */
export const getMiningResults = async (
  fileId: string,
  schemaHash: string
): Promise<MiningResult> => {
  const response = await apiClient.get(
    `/mining/${fileId.toUpperCase()}/mining-results?schema_hash=${schemaHash}`
  );
  return response.data;
};

/**
 * Record a correction for mining results
 */
export const submitCorrection = async (
  fileId: string,
  correction: MiningCorrection
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(`/mining/${fileId.toUpperCase()}/correct`, correction);
  return response.data;
};

/**
 * Get all corrections recorded for a file
 */
export const getCorrections = async (fileId: string): Promise<CorrectionsResponse> => {
  const response = await apiClient.get(`/mining/${fileId.toUpperCase()}/corrections`);
  return response.data;
};

// ============================================================
// ASCII Rendering API
// ============================================================

/**
 * Get ASCII pages for a file
 */
export const getAsciiPages = async (fileId: string, page?: number): Promise<AsciiPagesResponse> => {
  const params = page !== undefined ? `?page=${page}` : '';
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/ascii${params}`);
  return response.data;
};

/**
 * Trigger ASCII rendering for a file
 */
export const triggerAsciiRendering = async (fileId: string): Promise<AsciiJobStatus> => {
  const response = await apiClient.post(`/processing/${fileId.toUpperCase()}/ascii`, {});
  return response.data;
};

/**
 * Get ASCII rendering job status
 */
export const getAsciiJobStatus = async (fileId: string): Promise<AsciiJobStatus> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/ascii-job`);
  return response.data;
};

// ============================================================
// Sense Summary API
// ============================================================

/**
 * Get the AI-generated sense summary for a file
 */
export const getSenseSummary = async (fileId: string): Promise<SenseSummary> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/sense`);
  return response.data;
};

/**
 * Get the Groq Scout analysis result for a file
 */
export const getScoutResult = async (fileId: string): Promise<ScoutResult> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/scout`);
  return response.data;
};

/**
 * Get sense mining job status
 */
export const getSenseJobStatus = async (fileId: string): Promise<SenseJobStatus> => {
  const response = await apiClient.get(`/processing/${fileId.toUpperCase()}/sense-job`);
  return response.data;
};
